import React from 'react';
import ContentEditable from '../../utils/SafeContentEditable';
import { handleEditablePaste } from '../../utils/pasteUtils';
import { indentStyle } from '../../utils/indentUtils';
import { useMathPills } from '../../hooks/useMathPills';
import { useVariablePicker } from '../../hooks/useVariablePicker';
import { useTextBlockContent } from './hooks/useTextBlockContent';
import { useDocMentionPicker } from './hooks/useDocMentionPicker';
import { useSlashMenu } from './hooks/useSlashMenu';
import { useTextBlockInput } from './hooks/useTextBlockInput';

/**
 * TextBlock — an editable paragraph, and the home of the "/" quick-insert
 * menu.
 *
 * Keyboard behaviour (Notion-grade):
 *  - Enter splits the paragraph at the caret (text after the caret moves to
 *    the new paragraph); at the end it simply starts a fresh one.
 *  - Backspace at the very start outdents an indented paragraph, then merges
 *    into the previous block (caret lands at the junction); in an empty
 *    paragraph it deletes the block.
 *  - ArrowUp on the first line / ArrowDown on the last line moves the caret
 *    to the neighbouring block.
 *  - ArrowLeft at the start / ArrowRight at the end moves to the end of the
 *    previous block / start of the next.
 *
 * Doc mentions ("/doc", Chunk 10a): picking a doc drops an atomic inline tag
 * (contenteditable="false") into the paragraph; clicking a tag calls
 * `onOpenDoc(docId)` so the host can open the doc as a peek overlay.
 */
export const TextBlock = ({
  block,
  onUpdate,
  onInsertAfter,
  onUpdateAndInsert,
  onDelete,
  onMergeWithPrevious,
  onMergeWithNext,
  onNavigate,
  onEscapeContainer,
  onOpenDoc,
  resolveDocMeta,
  availableDocs,
  resolveVariable,
  availableVariables,
  onCreateVariable,
  hasVariablesBlock = false,
  slashExcludeIds = null,
}) => {
  const { content, setContent, contentRef, contentEditableRef } = useTextBlockContent({
    block,
    onUpdate,
    resolveDocMeta,
    resolveVariable,
  });

  const { mathMenu, openFromSlash } = useMathPills({
    blockId: block.id,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    availableVariables,
    onCreateVariable,
  });

  const { tryOpenFromBraceKey, openFromSlash: openVarFromSlash, variablePicker } =
    useVariablePicker({
      blockId: block.id,
      contentEditableRef,
      contentRef,
      setContent,
      onUpdate,
      resolveVariable,
      availableVariables,
      onCreateVariable,
    });

  const { openDocPicker, docPicker } = useDocMentionPicker({
    blockId: block.id,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    availableDocs,
  });

  const {
    slashMenuOpenRef,
    slashMenu,
    tryOpenFromSlashKey,
    updateSlashQuery,
    tryCloseSlashOnBackspace,
  } = useSlashMenu({
    block,
    contentEditableRef,
    onUpdate,
    onUpdateAndInsert,
    hasVariablesBlock,
    openDocPicker,
    openMathFromSlash: openFromSlash,
    openVarFromSlash,
    excludeIds: slashExcludeIds,
  });

  const { handleChange, handleKeyDown, handleEditableClick } = useTextBlockInput({
    block,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    onUpdateAndInsert,
    onInsertAfter,
    onDelete,
    onMergeWithPrevious,
    onMergeWithNext,
    onNavigate,
    onEscapeContainer,
    onOpenDoc,
    slashMenuOpenRef,
    tryOpenFromSlashKey,
    tryOpenFromBraceKey,
    updateSlashQuery,
    tryCloseSlashOnBackspace,
  });

  return (
    <>
      <ContentEditable
        innerRef={contentEditableRef}
        html={content}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={handleEditableClick}
        onPaste={handleEditablePaste}
        className="text-block doc-editable"
        tagName="p"
        style={indentStyle(block)}
        placeholder='Write here, or type "/" for blocks…'
      />

      {slashMenu}
      {docPicker}
      {variablePicker}
      {mathMenu}
    </>
  );
};
